import { Text } from '@/shared/ui';
import type { ReactNode } from 'react';
import { PlanIcon } from './plan-tab.icons';

interface Props {
  tone: 'alert' | 'invite';
  headline: string;
  /** The why line, and then the consequence. Either may be absent. */
  lines: string[];
  children?: ReactNode;
}

const TONE_COLOR = {
  alert: 'var(--accent-cobre)',
  invite: 'var(--seed-base)',
} as const;

/**
 * The shell both offers share: a light card with a coloured left border.
 *
 * The colour is the only alarm signal on the screen, which is why it is a
 * 3px border rather than a filled panel — what is still under control must
 * not be painted as a problem.
 *
 * @param props - Component props.
 * @returns The offer block.
 */
export function OfferBlock({ tone, headline, lines, children }: Props) {
  const Alert = PlanIcon.alert;

  return (
    <div
      className="flex flex-col gap-(--space-3-5)"
      data-testid={`offer-${tone}`}
      style={{
        padding: 'var(--space-4) var(--space-5)',
        border: '1px solid var(--border-subtle)',
        borderLeft: `3px solid ${TONE_COLOR[tone]}`,
        borderRadius: 'var(--radius-lg)',
        background: 'var(--surface-raised)',
      }}
    >
      <div className="flex items-start gap-(--space-3)">
        {tone === 'alert' && (
          <Text component="span" className="flex" style={{
            flex: 'none', marginTop: 'var(--space-0-5)', color: TONE_COLOR.alert,
          }}>
            <Alert size={18} strokeWidth={1.75} aria-hidden />
          </Text>
        )}
        <div className="flex flex-col gap-(--space-1) min-w-0">
          <Text component="span" style={{
            fontSize: 'var(--text-body)', fontWeight: 'var(--font-bold)',
            color: 'var(--text-primary)', lineHeight: 'var(--leading-snug)',
          }}>
            {headline}
          </Text>
          {lines.filter(Boolean).map((line) => (
            <Text component="span" key={line} style={{
              fontSize: 'var(--text-body-sm)', color: 'var(--text-secondary)',
              lineHeight: 'var(--leading-normal)',
            }}>
              {line}
            </Text>
          ))}
        </div>
      </div>
      {children}
    </div>
  );
}
